/**
 * DashboardProperties Component
 *
 * Properties tab showing the user's listings with quick actions.
 * Pulls live data from /api/properties?mine=true so phantom mock listings
 * never appear and IDs are real backend UUIDs (so View / Edit links work).
 */

import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../hooks/useAuth';
import { useUserProperties, type UserProperty } from '../../../hooks/useUserProperties';
import { apiRequest } from '../../../utils/apiClient';
import Card from '../../../components/base/Card';
import Badge from '../../../components/base/Badge';
import Tabs from '../../../components/base/Tabs';
import { GooeyLoader } from '../../../components/ui/gooey-loader';
import { formatAmount } from '../../../utils/numberFormat';

type PropertyStatusFilter = 'all' | 'active' | 'pending' | 'expired' | 'hidden';

// Normalize backend status (the response mapper outputs lowercase strings like
// 'available', 'pending', 'expired', 'internal', 'sold', 'rented', 'draft').
function normalizeStatus(raw: unknown): PropertyStatusFilter | 'sold' | 'rented' | 'draft' | 'suspended' | 'other' {
  const s = String(raw || '').toLowerCase();
  if (s === 'active' || s === 'available') return 'active';
  if (s === 'pending') return 'pending';
  if (s === 'expired') return 'expired';
  if (s === 'internal' || s === 'hidden') return 'hidden';
  if (s === 'sold') return 'sold';
  if (s === 'rented') return 'rented';
  if (s === 'draft') return 'draft';
  if (s === 'suspended') return 'suspended';
  return 'other';
}

export default function DashboardProperties() {
  const { t, i18n } = useTranslation('dashboard');
  const isRTL = i18n.language === 'ar';
  const { session } = useAuth();
  const { properties, loading, error, refetch } = useUserProperties();
  const [statusFilter, setStatusFilter] = useState<PropertyStatusFilter>('all');
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  const propertiesWithStatus = useMemo(() =>
    properties.map((p) => ({
      ...p,
      _normalized: normalizeStatus(p.status),
    })),
    [properties]
  );

  const filteredProperties = statusFilter === 'all'
    ? propertiesWithStatus
    : propertiesWithStatus.filter((p) => p._normalized === statusFilter);

  const counts = useMemo(() => ({
    all: propertiesWithStatus.length,
    active: propertiesWithStatus.filter((p) => p._normalized === 'active').length,
    pending: propertiesWithStatus.filter((p) => p._normalized === 'pending').length,
    expired: propertiesWithStatus.filter((p) => p._normalized === 'expired').length,
    hidden: propertiesWithStatus.filter((p) => p._normalized === 'hidden').length,
  }), [propertiesWithStatus]);

  const statusTabs = [
    { id: 'all', label: t('properties.allProperties'), badge: counts.all },
    { id: 'active', label: t('properties.activeProperties'), badge: counts.active },
    { id: 'pending', label: t('properties.pendingProperties'), badge: counts.pending },
    { id: 'expired', label: t('properties.expiredProperties'), badge: counts.expired },
    { id: 'hidden', label: isRTL ? 'مخفية' : 'Hidden', badge: counts.hidden },
  ];

  const getStatusBadge = (normalized: ReturnType<typeof normalizeStatus>) => {
    switch (normalized) {
      case 'active':
        return <Badge variant="success" size="sm">{isRTL ? 'نشط' : 'Active'}</Badge>;
      case 'pending':
        return <Badge variant="warning" size="sm">{isRTL ? 'معلق' : 'Pending'}</Badge>;
      case 'expired':
        return <Badge variant="error" size="sm">{isRTL ? 'منتهي' : 'Expired'}</Badge>;
      case 'hidden':
        return <Badge variant="info" size="sm">{isRTL ? 'مخفية' : 'Hidden'}</Badge>;
      case 'sold':
        return <Badge variant="info" size="sm">{isRTL ? 'مباع' : 'Sold'}</Badge>;
      case 'rented':
        return <Badge variant="info" size="sm">{isRTL ? 'مؤجر' : 'Rented'}</Badge>;
      case 'draft':
        return <Badge variant="default" size="sm">{isRTL ? 'مسودة' : 'Draft'}</Badge>;
      default:
        return null;
    }
  };

  const handleToggleVisibility = useCallback(async (property: UserProperty) => {
    if (!session?.token) return;
    const normalized = normalizeStatus(property.status);
    const nextStatus = normalized === 'hidden' ? 'active' : 'internal';
    setUpdatingId(property.id);
    try {
      await apiRequest(`/properties/${property.id}`, {
        method: 'PATCH',
        body: { status: nextStatus },
        token: session.token,
      });
      await refetch();
    } catch (err) {
      console.error('Failed to toggle property visibility', err);
      alert(isRTL ? 'فشل تحديث حالة العقار' : 'Failed to update property visibility');
    } finally {
      setUpdatingId(null);
    }
  }, [session?.token, refetch, isRTL]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <GooeyLoader primaryColor="#004D40" secondaryColor="#3C9F8F" borderColor="#E0E9E7" />
      </div>
    );
  }

  if (error) {
    return (
      <Card variant="bordered" padding="lg">
        <div className="text-center py-8">
          <i className="ri-error-warning-line text-4xl text-red-400 mb-3 block"></i>
          <p className="text-[var(--pv-color-text-muted)] mb-4">{error}</p>
          <button
            onClick={refetch}
            className="px-4 py-2 text-sm bg-[var(--pv-color-primary)] text-white rounded-lg hover:bg-[var(--pv-color-primary-dark)] transition-colors"
          >
            {isRTL ? 'إعادة المحاولة' : 'Retry'}
          </button>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Status Tabs */}
      <Tabs
        tabs={statusTabs}
        activeTab={statusFilter}
        onChange={(id) => setStatusFilter(id as PropertyStatusFilter)}
        variant="pills"
        size="sm"
      />

      {/* Properties Grid */}
      {filteredProperties.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredProperties.map((property) => {
            const isHidden = property._normalized === 'hidden';
            const isUpdating = updatingId === property.id;
            return (
              <Card
                key={property.id}
                variant="bordered"
                padding="none"
                className="overflow-hidden hover:shadow-lg transition-shadow"
              >
                {/* Property Image */}
                <div className="relative h-40 bg-gray-100 dark:bg-[var(--pv-color-surface-soft)]">
                  {property.imageUrl ? (
                    <img
                      src={property.imageUrl}
                      alt={property.title}
                      className={`w-full h-full object-cover ${isHidden ? 'opacity-50' : ''}`}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <i className="ri-image-line text-4xl text-gray-300"></i>
                    </div>
                  )}
                  <div className="absolute top-2 right-2">
                    {getStatusBadge(property._normalized)}
                  </div>
                </div>

                {/* Property Info */}
                <div className="p-4">
                  <h4 className="font-semibold text-[var(--pv-color-text)] line-clamp-1 mb-1">
                    {isRTL ? (property.titleAr || property.title) : property.title}
                  </h4>
                  <p className="text-sm text-[var(--pv-color-text-muted)] mb-3">
                    {property.address}, {property.city}
                  </p>

                  {/* Property Details Row */}
                  <div className="flex items-center gap-4 text-sm text-[var(--pv-color-text-muted)] mb-4">
                    <span className="flex items-center gap-1">
                      <i className="ri-hotel-bed-line"></i>
                      {property.bedrooms}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-drop-line"></i>
                      {property.bathrooms}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-ruler-line"></i>
                      {property.sqft ? formatAmount(Math.round(property.sqft / 10.7639)) : 0} م²
                    </span>
                  </div>

                  {/* Price */}
                  <div className="text-lg font-bold text-[var(--pv-color-primary)] mb-4">
                    {formatAmount(property.price)} ريال
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <Link
                      to={`/property/${property.id}`}
                      className="flex-1 text-center py-2 px-3 text-sm border border-[var(--pv-border-soft)] rounded-lg text-[var(--pv-color-text)] hover:bg-gray-50 dark:hover:bg-[var(--pv-color-surface-soft)] transition-colors"
                    >
                      <i className="ri-eye-line me-1"></i>
                      {t('properties.viewProperty')}
                    </Link>
                    <Link
                      to={`/property/${property.id}/edit`}
                      className="flex-1 text-center py-2 px-3 text-sm bg-[var(--pv-color-primary)] text-white rounded-lg hover:bg-[var(--pv-color-primary-dark)] transition-colors"
                    >
                      <i className="ri-edit-line me-1"></i>
                      {t('properties.editProperty')}
                    </Link>
                  </div>

                  {/* Hide / Show toggle */}
                  <button
                    onClick={() => handleToggleVisibility(property)}
                    disabled={isUpdating}
                    className={`w-full mt-2 py-2 px-3 text-sm rounded-lg transition-colors border ${
                      isHidden
                        ? 'border-emerald-500 text-emerald-600 hover:bg-emerald-50'
                        : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                    } disabled:opacity-50 disabled:cursor-wait`}
                  >
                    {isHidden ? (
                      <>
                        <i className="ri-eye-line me-1"></i>
                        {isRTL ? 'إظهار العقار' : 'Make Visible'}
                      </>
                    ) : (
                      <>
                        <i className="ri-eye-off-line me-1"></i>
                        {isRTL ? 'إخفاء العقار' : 'Hide Property'}
                      </>
                    )}
                  </button>

                  {/* Renew/Boost for expired */}
                  {property._normalized === 'expired' && (
                    <button className="w-full mt-2 py-2 px-3 text-sm border border-amber-500 text-amber-600 rounded-lg hover:bg-amber-50 transition-colors">
                      <i className="ri-refresh-line me-1"></i>
                      {t('properties.renewProperty')}
                    </button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card variant="bordered" padding="lg">
          <div className="text-center py-12">
            <i className="ri-building-line text-5xl text-gray-300 mb-4 block"></i>
            <h3 className="text-lg font-semibold text-[var(--pv-color-text)] mb-2">
              {t('properties.noProperties')}
            </h3>
            <p className="text-[var(--pv-color-text-muted)] mb-4">
              {t('properties.noPropertiesDescription')}
            </p>
            <Link
              to="/properties/new"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[var(--pv-color-primary)] text-white rounded-xl font-medium hover:shadow-lg transition-shadow"
            >
              <i className="ri-add-line"></i>
              {t('properties.addProperty')}
            </Link>
          </div>
        </Card>
      )}
    </div>
  );
}
